import { Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';

import ScheduleCardsLoader from './components/ScheduleCardsLoader';
import useScheduleCards from './hooks/useScheduleCards';
import {
  AmountStyled,
  CardWrapStyled,
  CardsWrapStyled,
  ClaimReportInnerContentStyled,
  InnerContentRowStyled,
} from './ScheduleCards.styled';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { ScheduleFilterValuesE, TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import AllScheduleIcon from 'icons/AllScheduleIcon';
import ArrowDownIcon from 'icons/ArrowDownIcon';

const enumMapping = [
  ScheduleFilterValuesE.LessThan31Days,
  ScheduleFilterValuesE.From31To60Days,
  ScheduleFilterValuesE.From61To90Days,
  ScheduleFilterValuesE.From91To120Days,
  ScheduleFilterValuesE.Over120Days,
];

const ScheduleCards = () => {
  const { scheduleReport, isLoadingScheduleReport, totalTicketAmount, totalTicketCount } =
    useScheduleCards();
  const { isMobile } = useResponsive();
  const {
    updateSearchParamsArray,
    selectedRoofTop,
    activeScheduleCards,
    setActiveScheduleCards,
    activeScheduleFilters,
    setActiveScheduleFilters,
    ticketsCountByRoofTop,
    scheduleCategoryId,
    setScheduleCategoryId,
  } = useTickets();
  const [isReportOpened] = useState(!isMobile);
  const [defaultRooftopId, setDefaultRooftopId] = useState<string>();

  useEffect(() => {
    // Set default rooftop ID based on ticketsCountByRoofTop
    if (ticketsCountByRoofTop && ticketsCountByRoofTop.countByRoofTop?.length > 0) {
      setDefaultRooftopId(ticketsCountByRoofTop.countByRoofTop[0].roofTopId);
    }
  }, [ticketsCountByRoofTop]);

  const categoriesResult = useFetchTicketRequestCategories(
    true,
    selectedRoofTop ?? defaultRooftopId
  );

  useEffect(() => {
    if (categoriesResult.data) {
      const scheduleCategory = categoriesResult.data.find(
        category => category.categoryName.toLowerCase() === 'schedule'
      );
      setScheduleCategoryId(scheduleCategory ? scheduleCategory.id : scheduleCategoryId);
    }
  }, [categoriesResult.data, scheduleCategoryId, setScheduleCategoryId]);

  const validCategoryIds = [scheduleCategoryId].filter(
    (id): id is number => typeof id === 'number'
  );

  // Sync active cards and filters with URL parameters on page load
  const searchParams = new URLSearchParams(window.location.search);
  const categoryIdsString = searchParams.getAll(TicketsRefinementKeysE.Category)[0]?.split(',');
  useEffect(() => {
    const newActiveCards: string[] = [];
    const newActiveFilters: ScheduleFilterValuesE[] = [];
    const scheduleRangeString = searchParams.getAll(TicketsRefinementKeysE.Schedule)[0]?.split(',');
    if (scheduleRangeString?.includes(ScheduleFilterValuesE.LessThan31Days)) {
      newActiveCards.push('dealer-1');
      newActiveFilters.push(ScheduleFilterValuesE.LessThan31Days);
    }
    if (scheduleRangeString?.includes(ScheduleFilterValuesE.From31To60Days)) {
      newActiveCards.push('dealer-2');
      newActiveFilters.push(ScheduleFilterValuesE.From31To60Days);
    }
    if (scheduleRangeString?.includes(ScheduleFilterValuesE.From61To90Days)) {
      newActiveCards.push('dealer-3');
      newActiveFilters.push(ScheduleFilterValuesE.From61To90Days);
    }
    if (scheduleRangeString?.includes(ScheduleFilterValuesE.From91To120Days)) {
      newActiveCards.push('dealer-4');
      newActiveFilters.push(ScheduleFilterValuesE.From91To120Days);
    }
    if (scheduleRangeString?.includes(ScheduleFilterValuesE.Over120Days)) {
      newActiveCards.push('dealer-5');
      newActiveFilters.push(ScheduleFilterValuesE.Over120Days);
    }
    if (categoryIdsString?.includes(validCategoryIds.toString())) {
      newActiveCards.push(
        'schedule-all',
        'dealer-1',
        'dealer-2',
        'dealer-3',
        'dealer-4',
        'dealer-5'
      );
    }

    setActiveScheduleCards(newActiveCards);
    setActiveScheduleFilters(newActiveFilters);
    // eslint-disable-next-line
  }, []);

  const toggleFilter = (cardKey: string, filterValue?: ScheduleFilterValuesE) => {
    let newActiveCards = [...activeScheduleCards];
    let newActiveFilters = [...activeScheduleFilters];
    const categoryIds =
      searchParams
        .get(TicketsRefinementKeysE.Category)
        ?.split(',')
        .map(Number)
        .filter(categoryId => categoryId !== scheduleCategoryId) || [];
    const categoryIdsIncludingSchedule = [...categoryIds, scheduleCategoryId!];

    if (cardKey === 'schedule-all') {
      if (newActiveCards.includes('schedule-all')) {
        // Deselect all cards
        newActiveCards = [];
        newActiveFilters = [];
        updateSearchParamsArray(TicketsRefinementKeysE.Schedule, null);
        updateSearchParamsArray(TicketsRefinementKeysE.Category, categoryIds);
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
      } else {
        // Select all cards
        newActiveCards = [
          'schedule-all',
          'dealer-1',
          'dealer-2',
          'dealer-3',
          'dealer-4',
          'dealer-5',
        ];
        newActiveFilters = [
          ScheduleFilterValuesE.LessThan31Days,
          ScheduleFilterValuesE.From31To60Days,
          ScheduleFilterValuesE.From61To90Days,
          ScheduleFilterValuesE.From91To120Days,
          ScheduleFilterValuesE.Over120Days,
        ];
        updateSearchParamsArray(TicketsRefinementKeysE.Category, categoryIdsIncludingSchedule);
      }
    } else {
      const currentIndex = newActiveCards.indexOf(cardKey);

      if (currentIndex === -1) {
        newActiveCards.push(cardKey);
        if (filterValue) {
          newActiveFilters.push(filterValue);
        }
      } else {
        newActiveCards.splice(currentIndex, 1);
        if (filterValue) {
          const filterIndex = newActiveFilters.indexOf(filterValue);
          if (filterIndex !== -1) {
            newActiveFilters.splice(filterIndex, 1);
          }
        }
      }

      // Ensure the "All" card is inactive if any individual card is deselected
      const allIndex = newActiveCards.indexOf('schedule-all');
      if (allIndex !== -1) {
        newActiveCards.splice(allIndex, 1);
      }

      // Activate the "All" card if all individual cards are selected
      if (
        newActiveCards.includes('dealer-1') &&
        newActiveCards.includes('dealer-2') &&
        newActiveCards.includes('dealer-3') &&
        newActiveCards.includes('dealer-4') &&
        newActiveCards.includes('dealer-5')
      ) {
        newActiveCards.push('schedule-all');
        updateSearchParamsArray(TicketsRefinementKeysE.Category, categoryIdsIncludingSchedule);
      }
    }

    setActiveScheduleCards(newActiveCards);
    setActiveScheduleFilters(newActiveFilters);
    updateSearchParamsArray(
      TicketsRefinementKeysE.Schedule,
      newActiveFilters.length > 0 && newActiveFilters.length < 5 ? newActiveFilters : null
    );
    updateSearchParamsArray(
      TicketsRefinementKeysE.Category,
      newActiveFilters.length > 0 ? categoryIdsIncludingSchedule : categoryIds
    );
  };

  return (
    <CardsWrapStyled justify="start" align="stretch" isReportOpened={isReportOpened}>
      {isLoadingScheduleReport ? (
        <ScheduleCardsLoader />
      ) : (
        <>
          <Tooltip
            key={'tooltip-all'}
            title={
              scheduleReport?.totalDataBySubCategories &&
              scheduleReport.totalDataBySubCategories.length > 0 ? (
                <div>
                  {scheduleReport.totalDataBySubCategories?.map((sub, subIndex) => (
                    <FlexContainer key={`sub-${subIndex}`} justify="between" style={{ gap: 12 }}>
                      <p>{sub.subCategoryName}</p>
                      <p>{sub.count}</p>
                    </FlexContainer>
                  ))}
                </div>
              ) : (
                ''
              )
            }
            arrow
            placement="bottom"
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -16], //reduce vertical gap
                  },
                },
              ],
            }}
          >
            <div>
              <CardWrapStyled
                onClick={() => toggleFilter('schedule-all')}
                isActive={activeScheduleCards.includes('schedule-all')}
                direction="column"
                justify="start"
                key="schedule-all"
              >
                <ClaimReportInnerContentStyled align="start" direction="column">
                  <InnerContentRowStyled
                    style={{ height: '20px' }}
                    align="center"
                    justify="between"
                  >
                    <Typography>
                      Schedule as of
                      {scheduleReport?.lastUpdatedDateTime && (
                        <Typography variant="h5">
                          {scheduleReport
                            ? format(new Date(scheduleReport.lastUpdatedDateTime), 'MMMM dd, yyyy')
                            : ''}
                        </Typography>
                      )}
                    </Typography>
                    <AllScheduleIcon />
                  </InnerContentRowStyled>
                  <InnerContentRowStyled align="end" justify="between">
                    <AmountStyled variant="h5" title={totalTicketAmount?.toLocaleString()}>
                      ${totalTicketAmount?.toLocaleString()}
                    </AmountStyled>
                    <div>
                      <Typography variant="body5" color="darkGrey">
                        Tickets
                      </Typography>
                      <Typography variant="h4">{totalTicketCount}</Typography>
                    </div>
                  </InnerContentRowStyled>
                </ClaimReportInnerContentStyled>
              </CardWrapStyled>
            </div>
          </Tooltip>
          {scheduleReport?.data.map((report, index: number) => (
            <>
              <Tooltip
                key={`tooltip-${index + 1}`}
                title={
                  report.dataBySubCategories && report.dataBySubCategories.length > 0 ? (
                    <div>
                      {report.dataBySubCategories?.map((sub, subIndex) => (
                        <FlexContainer
                          key={`sub-${subIndex}`}
                          justify="between"
                          style={{ gap: 12 }}
                        >
                          <p>{sub.subCategoryName}</p>
                          <p>{sub.count}</p>
                        </FlexContainer>
                      ))}
                    </div>
                  ) : (
                    ''
                  )
                }
                arrow
                placement="bottom"
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -16], //reduce vertical gap
                      },
                    },
                  ],
                }}
              >
                <div>
                  <CardWrapStyled
                    key={`dealer-${index + 1}`}
                    onClick={() => toggleFilter(`dealer-${index + 1}`, enumMapping[index])}
                    isActive={activeScheduleCards.includes(`dealer-${index + 1}`)}
                    direction="column"
                    justify="start"
                  >
                    <ClaimReportInnerContentStyled align="start" direction="column">
                      <InnerContentRowStyled align="center" justify="between">
                        <Typography variant="h5" color="darkGrey" weight="normal">
                          {report.ageRange.from}
                          {report.ageRange.to ? `-${report.ageRange.to}` : '+'} Days
                        </Typography>
                        <ArrowDownIcon />
                      </InnerContentRowStyled>
                      <InnerContentRowStyled align="end" justify="between">
                        <AmountStyled variant="h5" title={report.amount?.toLocaleString()}>
                          ${report.amount?.toLocaleString()}
                        </AmountStyled>
                        <div>
                          <Typography variant="body5" color="darkGrey">
                            Tickets
                          </Typography>
                          <Typography variant="h4">{report.count}</Typography>
                        </div>
                      </InnerContentRowStyled>
                    </ClaimReportInnerContentStyled>
                  </CardWrapStyled>
                </div>
              </Tooltip>
            </>
          ))}
        </>
      )}
    </CardsWrapStyled>
  );
};

export default ScheduleCards;
